import { HelpCircle, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

interface GenericClarification {
  context: string | null
  questions: { question: string; options: string[] }[]
}

export interface ClarificationCardProps {
  clarification: GenericClarification
  onSubmit: (answers: { question: string; answer: string }[]) => Promise<void>
  busy: boolean
  secondaryAction?: { label: string; onClick: () => Promise<void>; busy: boolean }
  translationPrefix?: 'schemaAgent.clarification' | 'osint.clarification'
}

/** Shared free-text / free-string-option clarification card for autonomous runs. */
export function ClarificationCard({
  clarification,
  onSubmit,
  busy,
  secondaryAction,
  translationPrefix = 'schemaAgent.clarification',
}: ClarificationCardProps) {
  const { t } = useTranslation()
  const [values, setValues] = useState<string[]>(() => clarification.questions.map(() => ''))

  useEffect(() => {
    setValues(clarification.questions.map(() => ''))
  }, [clarification.questions])

  const canSubmit = !busy && values.some(value => value.trim().length > 0)
  const submit = async () => {
    if (!canSubmit) return
    await onSubmit(
      clarification.questions.map((item, index) => ({
        question: item.question,
        answer: values[index]?.trim() ?? '',
      }))
    )
  }

  const update = (index: number, value: string) => {
    setValues(current => {
      const next = [...current]
      next[index] = value
      return next
    })
  }

  return (
    <Card tone="primary" className="flex flex-col gap-4 p-4 md:p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <HelpCircle className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">
            {t(`${translationPrefix}.title`)}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {clarification.context || t(`${translationPrefix}.description`)}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {clarification.questions.map((item, index) => (
          <div key={item.question} className="flex flex-col gap-2">
            <Label htmlFor={`schema-clarify-${index}`} className="max-md:leading-snug">
              {item.question}
            </Label>
            {item.options.length > 0 && (
              <RadioGroup
                value={values[index] ?? ''}
                onValueChange={value => update(index, value)}
                disabled={busy}
                className="gap-2"
                aria-label={item.question}
              >
                {item.options.map(option => (
                  <label
                    key={option}
                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-muted/60"
                  >
                    <RadioGroupItem value={option} />
                    {option}
                  </label>
                ))}
              </RadioGroup>
            )}
            <Input
              id={`schema-clarify-${index}`}
              value={values[index] ?? ''}
              onChange={event => update(index, event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void submit()
              }}
              placeholder={
                item.options.length > 0
                  ? t('schemaAgent.clarification.otherPlaceholder')
                  : t(`${translationPrefix}.answerPlaceholder`)
              }
              disabled={busy}
              autoComplete="off"
              className="max-md:text-ellipsis max-md:placeholder:text-sm"
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {secondaryAction && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void secondaryAction.onClick()}
            loading={secondaryAction.busy}
            disabled={busy}
          >
            {secondaryAction.label}
          </Button>
        )}
        <Button onClick={() => void submit()} loading={busy} disabled={!canSubmit}>
          {!busy && <Send className="size-4" />}
          {t(`${translationPrefix}.submit`)}
        </Button>
      </div>
    </Card>
  )
}
